import { useCallback, useEffect, useState } from "react";
import type { ActivityEntry } from "../types";

interface Props {
  isSysadmin: boolean;
  /** Org escolhida no seletor (sysadmin só) - sem isto, mostra a visão da própria org (ou da plataforma inteira, se sysadmin). */
  orgId?: string;
  onNavigate: (tab: string) => void;
}

interface BillingSummary {
  configured: boolean;
  linked: boolean;
  planId: string | null;
  planName: string | null;
  status: string | null;
  allowed: boolean;
  spentUsdThisMonth: number;
  limits: { seats: number | null; workspaces: number | null; repos: number | null; budgetUsdMonthly: number | null };
  usage: { seats: number; workspaces: number; repos: number };
}

interface AgentSpend {
  costUsd: number;
  tokens: number;
  responses: number;
}

interface UserRow {
  id: string;
  email: string;
  name: string;
  status: "pending" | "approved" | "rejected" | "inactive";
  orgId?: string;
  totalCostUsd: number;
}

interface OverviewExtras {
  trend: { costThisWeek: number; costPrevWeek: number; asksThisWeek: number; asksPrevWeek: number };
  engines?: Record<"claude" | "codex" | "gemini", AgentSpend>;
  /** Consumo do mês com a geração automática de documentação. */
  docGen?: { costUsd: number; tokens: number; runs: number };
  staleDocs: number;
  totalRepos: number;
  blocks: { count: number; recent: { ts: string; type: string; userEmail: string }[] };
  health: { claude: boolean; codex: boolean; maintenanceActive: boolean };
}

function fmtTokensShort(n: number): string {
  if (!n) return "0";
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(1)}k`;
  return `${(n / 1_000_000).toFixed(2)}M`;
}

/** Variação percentual entre dois períodos, com rótulo e sinal. */
function deltaLabel(cur: number, prev: number): { text: string; up: boolean | null } {
  if (prev === 0 && cur === 0) return { text: "sem variação", up: null };
  if (prev === 0) return { text: "novo", up: true };
  const pct = Math.round(((cur - prev) / prev) * 100);
  if (pct === 0) return { text: "estável", up: null };
  return { text: `${pct > 0 ? "+" : ""}${pct}% vs. 7d anteriores`, up: pct > 0 };
}

interface OrgRow {
  id: string;
  name: string;
  seats: { count: number; limit: number | null }; // null = ilimitado
  billingLinked: boolean;
}

const STATUS_PT: Record<string, string> = {
  trialing: "em teste",
  active: "ativa",
  past_due: "pagamento pendente",
  canceled: "cancelada",
};

const ACTIVITY_LABEL: Record<string, string> = {
  register: "cadastrou-se",
  login: "entrou",
  login_failed: "login recusado",
  login_blocked: "login bloqueado",
  logout: "saiu",
  ask: "perguntou",
  regenerate: "regenerou",
  docgen: "gerou documentação",
  approve: "aprovou",
  reject: "rejeitou",
  deactivate: "inativou",
  view_other: "abriu de outro",
  blocked: "bloqueado (403)",
};

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR", { timeZone: "America/Sao_Paulo" });
  } catch {
    return iso;
  }
}

export function OverviewPanel({ isSysadmin, orgId, onNavigate }: Props) {
  const [users, setUsers] = useState<UserRow[] | null>(null);
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [activityFeed, setActivityFeed] = useState<ActivityEntry[]>([]);
  const [orgs, setOrgs] = useState<OrgRow[] | null>(null);
  const [extras, setExtras] = useState<OverviewExtras | null>(null);
  const [loading, setLoading] = useState(true);

  const [maintenanceActive, setMaintenanceActive] = useState(false);
  const [maintenanceMessage, setMaintenanceMessage] = useState("");
  const [maintenanceUntil, setMaintenanceUntil] = useState("");
  const [savingMaintenance, setSavingMaintenance] = useState(false);
  const [maintenanceSaved, setMaintenanceSaved] = useState(false);

  useEffect(() => {
    if (!isSysadmin) return;
    fetch("/api/status")
      .then((r) => (r.ok ? r.json() : null))
      .then((j: { maintenanceActive?: boolean; maintenanceMessage?: string; maintenanceUntil?: string | null } | null) => {
        if (!j) return;
        setMaintenanceActive(Boolean(j.maintenanceActive));
        setMaintenanceMessage(j.maintenanceMessage ?? "");
        setMaintenanceUntil(j.maintenanceUntil ? j.maintenanceUntil.slice(0, 16) : "");
      })
      .catch(() => {});
  }, [isSysadmin]);

  const saveMaintenance = async () => {
    setSavingMaintenance(true);
    setMaintenanceSaved(false);
    try {
      await fetch("/api/admin/system-status", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          maintenanceActive,
          maintenanceMessage,
          maintenanceUntil: maintenanceUntil ? new Date(maintenanceUntil).toISOString() : null,
        }),
      });
      setMaintenanceSaved(true);
      setTimeout(() => setMaintenanceSaved(false), 2500);
    } finally {
      setSavingMaintenance(false);
    }
  };

  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";
  const qsAmp = orgId ? `&orgId=${encodeURIComponent(orgId)}` : "";
  const platformWide = isSysadmin && !orgId;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [usersR, billingR, activityR, orgsR, extrasR] = await Promise.all([
        fetch(`/api/admin/users${qs}`),
        fetch(`/api/admin/billing${qs}`),
        fetch(`/api/admin/activity?limit=6${qsAmp}`),
        platformWide ? fetch("/api/admin/organizations") : Promise.resolve(null),
        fetch(`/api/admin/overview${qs}`),
      ]);
      if (usersR.ok) setUsers(await usersR.json());
      if (billingR.ok) setBilling(await billingR.json());
      if (activityR.ok) setActivityFeed((await activityR.json()).items ?? []);
      if (orgsR?.ok) setOrgs((await orgsR.json()).items ?? []);
      if (extrasR.ok) setExtras(await extrasR.json());
    } finally {
      setLoading(false);
    }
  }, [qs, qsAmp, platformWide]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading && !users) {
    return (
      <div className="admin-overview-section">
        <div className="stats-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div className="stat-card" key={i}>
              <div className="stat-card-value">…</div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const allUsers = users ?? [];
  const topSpenders = [...allUsers]
    .filter((u) => (u.totalCostUsd || 0) > 0)
    .sort((a, b) => (b.totalCostUsd || 0) - (a.totalCostUsd || 0))
    .slice(0, 5);
  const pendingCount = allUsers.filter((u) => u.status === "pending").length;
  const approvedCount = allUsers.filter((u) => u.status === "approved").length;
  const totalSpend = allUsers.reduce((acc, u) => acc + (u.totalCostUsd || 0), 0);
  const seatsLimit = billing?.limits.seats ?? null;
  const seatsUsed = billing?.usage.seats ?? approvedCount;
  const seatsLabel =
    seatsLimit === null || !Number.isFinite(seatsLimit) ? `${seatsUsed} · ilimitado` : `${seatsUsed}/${seatsLimit}`;
  const wsUsed = billing?.usage.workspaces ?? null;
  const wsLimit = billing?.limits.workspaces ?? null;
  const reposUsed = billing?.usage.repos ?? null;
  const reposLimit = billing?.limits.repos ?? null;
  const budget = billing?.limits.budgetUsdMonthly ?? null;
  const spent = billing?.spentUsdThisMonth ?? totalSpend;
  const budgetPct = budget && budget > 0 ? Math.round((spent / budget) * 100) : null;

  return (
    <>
      <div className="admin-overview-section">
        <h2>{platformWide ? "Plataforma" : "Esta organização"}</h2>
        <div className="stats-grid">
          {platformWide && orgs && (
            <StatCard label="Organizações" value={String(orgs.length)} hint={`${orgs.filter((o) => o.billingLinked).length} com billing vinculado`} />
          )}
          {!platformWide && billing && (
            <StatCard
              label="Plano"
              value={billing.planName ?? "—"}
              hint={billing.linked ? `assinatura ${STATUS_PT[billing.status ?? ""] ?? billing.status ?? "—"}` : "plano local"}
            />
          )}
          <StatCard
            label={platformWide ? "Contas na plataforma" : "Assentos em uso"}
            value={platformWide ? String(allUsers.length) : seatsLabel}
            hint={platformWide ? `${approvedCount} aprovada(s)` : `${approvedCount} aprovada(s), ${pendingCount} pendente(s)`}
          />
          {wsUsed !== null && (
            <StatCard
              label="Workspaces"
              value={wsLimit === null ? String(wsUsed) : `${wsUsed}/${wsLimit}`}
              hint={wsLimit === null ? "sem limite" : "do plano"}
            />
          )}
          {reposUsed !== null && (
            <StatCard
              label="Repositórios"
              value={reposLimit === null ? String(reposUsed) : `${reposUsed}/${reposLimit}`}
              hint={reposLimit === null ? "conectados" : "conectados, do plano"}
            />
          )}
          <StatCard
            label="Aguardando aprovação"
            value={String(pendingCount)}
            hint={pendingCount > 0 ? "clique em Usuários pra revisar" : "tudo em dia"}
          />
          <StatCard
            label="Gasto de IA no mês"
            value={`$${spent.toFixed(2)}`}
            hint={budget != null ? `${budgetPct}% de $${budget}` : "sem limite definido"}
          />
        </div>
        {billing?.linked && !billing.allowed && (
          <div className="auth-error" style={{ marginTop: 10 }}>
            Assinatura {STATUS_PT[billing.status ?? ""] ?? billing.status} - o acesso pode estar limitado. Veja em Plano e billing.
          </div>
        )}
      </div>

      {/* Tendência 7d vs 7d + indicadores de saúde/doc/bloqueios */}
      {extras && (
        <div className="admin-overview-section">
          <h2>Tendências e saúde</h2>
          <div className="stats-grid">
            <StatCard
              label="Perguntas (7 dias)"
              value={String(extras.trend.asksThisWeek)}
              hint={deltaLabel(extras.trend.asksThisWeek, extras.trend.asksPrevWeek).text}
            />
            <StatCard
              label="Gasto de IA (7 dias)"
              value={`$${extras.trend.costThisWeek.toFixed(2)}`}
              hint={deltaLabel(extras.trend.costThisWeek, extras.trend.costPrevWeek).text}
            />
            <StatCard
              label="Docs desatualizadas"
              value={`${extras.staleDocs}${extras.totalRepos ? `/${extras.totalRepos}` : ""}`}
              hint={extras.staleDocs > 0 ? "repositórios sem doc no último commit" : "documentação em dia"}
            />
            <StatCard
              label="Geração de docs (mês)"
              value={`${fmtTokensShort(extras.docGen?.tokens ?? 0)} tokens`}
              hint={`${extras.docGen?.runs ?? 0} geração(ões) · $${(extras.docGen?.costUsd ?? 0).toFixed(2)}`}
            />
            <StatCard
              label="Bloqueios (7 dias)"
              value={String(extras.blocks.count)}
              hint={extras.blocks.count > 0 ? "acessos negados/anomalias" : "nenhuma anomalia"}
            />
          </div>
          <div className="overview-health" style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 10 }}>
            <HealthChip ok={extras.health.claude} label="Claude" />
            <HealthChip ok={extras.health.codex} label="Codex" />
            <HealthChip ok={!extras.health.maintenanceActive} label={extras.health.maintenanceActive ? "Em manutenção" : "Operacional"} />
          </div>
        </div>
      )}

      {/* De onde vem o gasto de IA: Claude vs Codex no mês corrente */}
      {extras?.engines && (["claude", "codex", "gemini"] as const).some((ag) => (extras.engines?.[ag]?.responses ?? 0) > 0) && (
        <div className="admin-block">
          <h3>Gasto por motor de IA (mês)</h3>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {(["claude", "codex", "gemini"] as const).filter((ag) => extras.engines![ag]).map((ag) => {
              const e = extras.engines![ag];
              const total = extras.engines!.claude.costUsd + extras.engines!.codex.costUsd + (extras.engines!.gemini?.costUsd ?? 0);
              const pct = total > 0 ? Math.round((e.costUsd / total) * 100) : 0;
              return (
                <div key={ag} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{ width: 64, fontWeight: 600, textTransform: "capitalize" }}>{ag}</span>
                  <div style={{ flex: 1, height: 8, borderRadius: 999, background: "var(--bg-subtle)", overflow: "hidden" }}>
                    <div
                      style={{
                        width: `${pct}%`,
                        height: "100%",
                        borderRadius: 999,
                        background: ag === "claude" ? "var(--accent)" : ag === "gemini" ? "var(--warn)" : "var(--success)",
                        transition: "width 0.3s ease",
                      }}
                    />
                  </div>
                  <span className="admin-user-meta" style={{ width: 190, textAlign: "right" }}>
                    ${e.costUsd.toFixed(2)} · {e.responses} resposta{e.responses === 1 ? "" : "s"} ({pct}%)
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Top usuários por gasto */}
      {allUsers.length > 0 && topSpenders.length > 0 && (
        <div className="admin-block">
          <h3>Top usuários por gasto de IA</h3>
          <div className="admin-activity">
            {topSpenders.map((u, i) => (
              <div key={u.id} className="admin-act-row">
                <span className="admin-act-ts">#{i + 1}</span>
                <span className="admin-act-detail" style={{ flex: 1 }}>{u.name || u.email}</span>
                <span className="admin-act-type">${u.totalCostUsd.toFixed(2)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Bloqueios/anomalias recentes */}
      {extras && extras.blocks.recent.length > 0 && (
        <div className="admin-block">
          <h3>Bloqueios e anomalias (7 dias)</h3>
          <div className="admin-activity">
            {extras.blocks.recent.map((b, i) => (
              <div key={`${b.ts}-${i}`} className="admin-act-row">
                <span className="admin-act-ts">{fmtDate(b.ts)}</span>
                <span className={`admin-act-type type-${b.type}`}>{ACTIVITY_LABEL[b.type] ?? b.type}</span>
                <span className="admin-act-detail">{b.userEmail}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="admin-overview-section">
        <h2>Atalhos</h2>
        <div className="admin-quick-actions">
          <button className="btn-approve" type="button" onClick={() => onNavigate("users")}>
            Ver usuários
          </button>
          <button className="btn-reject" type="button" onClick={() => onNavigate("billing")}>
            Plano e billing
          </button>
          {isSysadmin && (
            <button className="btn-reject" type="button" onClick={() => onNavigate("organizations")}>
              Organizações
            </button>
          )}
        </div>
      </div>

      {isSysadmin && (
        <div className="admin-block">
          <h3>Status do sistema</h3>
          <p className="admin-hint">
            Controla o banner de manutenção exibido a todo mundo e a{" "}
            <a href="/status" target="_blank" rel="noreferrer">
              página pública /status
            </a>
            .
          </p>
          <label className="maintenance-toggle">
            <input
              type="checkbox"
              checked={maintenanceActive}
              onChange={(e) => setMaintenanceActive(e.target.checked)}
            />
            Manutenção ativa (mostra aviso pra todas as organizações)
          </label>
          <textarea
            className="maintenance-message"
            placeholder="Mensagem do aviso (ex.: manutenção programada das 22h às 23h)"
            value={maintenanceMessage}
            onChange={(e) => setMaintenanceMessage(e.target.value)}
            rows={2}
          />
          <div className="maintenance-row">
            <label>
              Até (opcional)
              <input
                type="datetime-local"
                value={maintenanceUntil}
                onChange={(e) => setMaintenanceUntil(e.target.value)}
              />
            </label>
            <button type="button" className="btn-approve" onClick={() => void saveMaintenance()} disabled={savingMaintenance}>
              {savingMaintenance ? "Salvando…" : maintenanceSaved ? "Salvo!" : "Salvar"}
            </button>
          </div>
        </div>
      )}

      <div className="admin-block">
        <h3>Atividade recente</h3>
        {activityFeed.length === 0 ? (
          <div className="admin-empty small">Sem registros ainda.</div>
        ) : (
          <div className="admin-activity">
            {activityFeed.map((a, i) => (
              <div key={`${a.ts}-${i}`} className="admin-act-row">
                <span className="admin-act-ts">{fmtDate(a.ts)}</span>
                <span className={`admin-act-type type-${a.type}`}>{ACTIVITY_LABEL[a.type] ?? a.type}</span>
                <span className="admin-act-detail">{a.userEmail}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function HealthChip({ ok, label }: { ok: boolean; label: string }) {
  return (
    <span
      className="health-chip"
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        borderRadius: 999,
        fontSize: "0.8rem",
        border: "1px solid var(--border, #d8dade)",
        color: ok ? "var(--success)" : "var(--danger, #d64545)",
      }}
    >
      <span
        aria-hidden
        style={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          background: ok ? "var(--success)" : "var(--danger, #d64545)",
        }}
      />
      {label}
    </span>
  );
}

function StatCard({ label, value, hint }: { label: string; value: string; hint?: string }) {
  return (
    <div className="stat-card">
      <div className="stat-card-value">{value}</div>
      <div className="stat-card-label">{label}</div>
      {hint && <div className="stat-card-hint">{hint}</div>}
    </div>
  );
}
